import { Formik, Form, Field } from "formik";
import { useState } from "react";
import SectionDescription from "components/lib/Shared/SectionDescription";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import Input from "components/lib/Shared/Input";
import { handleFilesUpload } from "utils/filesUtils";
import { updateTransaction } from "state/slices/transaction";
import { ChevronLeftIcon } from "@heroicons/react/outline";
import ESignatureManualDropDown from "components/lib/Shared/ESignatureManualDropDown";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { addComment, editComment } from "state/slices/comment";
import _get from "lodash/get";
import { Menu, Transition } from "@headlessui/react";
import { Fragment, useContext, useEffect } from "react";
import { usePopper } from "hooks/usePopper";
import { ChevronDownIcon } from "@heroicons/react/outline";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { useToast } from "hooks/useToast";
import { useDispatch, useSelector } from "react-redux";
import {
  brsUploadsValidationSchema,
  getFileUploadsValidationSchemaAsPerTypeOfChange,
} from "./validationSchemas";
import {
  getDocumentsAsPerTypeOfChange,
  brsFilingReceipt,
  courtOrderDocuments,
} from "./transactionDocumentData";
import { Constants } from "config/constants";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";
import ManualTransactionReviewContext from "context/ManualTransactionReviewContext";
import CommentsList from "components/lib/Global/CommentsList";
import FormFieldsCard from "components/lib/Global/FormFieldsCard";
import { ChevronRightIcon } from "@heroicons/react/solid";
import Checkbox from "components/lib/Shared/Checkbox";

const TransactionUpload = ({
  transaction,
  goToNextStep,
  goToPrevStep,
  isLastStep,
}) => {
  /** Company data */
  const { company } = useSelector((state) => state.companySlice);
  const { comments } = useSelector((state) => state.commentsSlice);
  const { shares } = useSelector((state) => state).sharesTableSlice;

  /** Redux hooks to manage state and dispatch actions */
  const dispatch = useDispatch();
  const storageKey = `BRS-${transaction._id}`;
  let localStorageBrsValues = {};
  if (JSON.parse(localStorage.getItem(storageKey))) {
    localStorageBrsValues = JSON.parse(localStorage.getItem(storageKey));
    Object.entries(localStorageBrsValues).forEach(([key, value]) => {
      const { name, type, lastModified, dataUrl } = localStorageBrsValues[key];
      const base64 = dataUrl.split(",")[1];
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      localStorageBrsValues[key] = new File([bytes], name, {
        type,
        lastModified,
      });
    });
  }

  /** toast hook for alert messages */
  const { toast } = useToast();

  /** react-router-dom hooks */
  const history = useHistory();
  /** Check if transaction has reached the super admin */

  let changedOfficial = {};
  if (transaction?.individualOfficials?.length > 0) {
    changedOfficial = transaction?.individualOfficials?.find((official) => {
      if (
        official?.changedFields &&
        Object.keys(official?.changedFields).length > 0
      ) {
        return official;
      }
    });
  }

  if (transaction?.corporateOfficials?.length > 0) {
    changedOfficial = transaction?.corporateOfficials?.find((official) => {
      if (
        official?.changedFields &&
        Object.keys(official?.changedFields).length > 0
      ) {
        return official;
      }
    });
  }

  if (transaction?.beneficialOwners?.length > 0) {
    changedOfficial = transaction?.beneficialOwners?.find((official) => {
      if (
        official?.changedFields &&
        Object.keys(official?.changedFields).length > 0
      ) {
        return official;
      }
    });
  }

  let changedFields = {};
  if (changedOfficial?.changedFields) {
    changedFields = changedOfficial?.changedFields;
  }

  const documents = getDocumentsAsPerTypeOfChange(
    transaction,
    changedFields,
    company
  ); // eg. APPOINTMENT of Authorized Person

  const brsReciptDocs = brsFilingReceipt();
  const courtOrderDocs = courtOrderDocuments();

  let fileNames = {};
  documents.forEach((doc) => {
    fileNames[doc.name] = "";
  });

  let fileUploads = {};
  let preSignedBusinessDocuments = {};

  if (
    transaction?.fileUploads &&
    Object.keys(transaction?.fileUploads).length > 0
  ) {
    fileUploads = transaction.fileUploads;
  } else {
    fileUploads = {
      ...fileNames,
    };
  }

  if (
    transaction?.preSignedBusinessDocuments &&
    Object.keys(transaction?.preSignedBusinessDocuments).length > 0
  ) {
    preSignedBusinessDocuments = transaction.preSignedBusinessDocuments;
  } else {
    preSignedBusinessDocuments = {
      ...fileNames,
    };
  }

  const [initialValues, setInitialValues] = useState({
    brsReferenceNumber: transaction?.brsReferenceNumber || "",
    preSignedBusinessDocuments,
    fileUploads: {
      ...fileUploads,
      ...localStorageBrsValues,
    },
    upholdConversionResolution:
      transaction?.companyChangedFields?.upholdConversionResolution || false,
    cancelConversionResolution:
      transaction?.companyChangedFields?.cancelConversionResolution || false,
    registrarApproved:
      transaction?.companyChangedFields?.registrarApproved || false,
    registrarRejected:
      transaction?.companyChangedFields?.registrarRejected || false,
  });

  /** Initial Formik values */

  /** Use for the dropdown button */
  const [trigger, container] = usePopper({
    placement: "bottom-end",
    strategy: "fixed",
    modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
  });

  /** Validation schema for Formik */
  let validationSchema = getFileUploadsValidationSchemaAsPerTypeOfChange(
    "fileUploads",
    transaction,
    changedFields
  );

  const transactionComments = comments.filter(
    (comment) => comment.transaction === transaction._id
  );
  const brsFormUploadComments = transactionComments.filter(
    (comment) => comment.formName === "brsFormUpload"
  );

  /** handle form events */

  const handleGoBack = () => {
    goToPrevStep();
  };

  /** Handle form submission */
  const handleSubmit = async (formData) => {
    let transactionData = {};
    const uploadedFiles = await handleFilesUpload(formData.fileUploads);
    transactionData = {
      fileUploads: {
        ...uploadedFiles,
      },
      brsReferenceNumber: formData?.brsReferenceNumber,
    };

    if (
      transaction?.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.AWAITING_COURT_ORDER
    ) {
      const companyChangedFields = {
        ...transaction.companyChangedFields,
        upholdConversionResolution: formData?.upholdConversionResolution,
        cancelConversionResolution: formData?.cancelConversionResolution,
      };
      transactionData = {
        ...transactionData,
        companyChangedFields,
      };
    }
    localStorage.removeItem(storageKey);

    const updatedTransaction = await dispatch(
      updateTransaction({
        trxId: transaction?._id,
        data: {
          ...transactionData,
        },
        queryParams: {
          updateRelatedTransactions: true, // update batched transactions
        },
      })
    );

    goToNextStep();
    toast("success", "Updated transaction successfully.");
  };

  return (
    <div>
      <div>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => handleSubmit(values)}
        >
          {({
            isSubmitting,
            values,
            errors,
            setFieldValue,
            submitForm,
            touched,
          }) => {
            return (
              <Form className="mt-4">
                <PersistFileUploadsToLocalStorage
                  fileUploads={values.fileUploads}
                  storageKey={storageKey}
                />
                <div className="flex gap-4">
                  {
                    <div className="w-full">
                      <SectionDescription
                        title="Signed BRS forms"
                        description="Please upload the incorporation forms generated from the BRS Portal, to be signed by the promoters and subscribers of the Company."
                      />
                      <div className="bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
                        {brsFormUploadComments.length > 0 && (
                          <div className="px-8 pt-5 pb-6 mt-4 bg-white">
                            <CommentsList
                              comments={brsFormUploadComments}
                              toggleCommentStatus={true}
                            />
                            <p className="mt-6 text-sm text-primary">
                              Comments: {brsFormUploadComments.length} of{" "}
                              {brsFormUploadComments.length}
                            </p>
                          </div>
                        )}
                        <div className="px-4 py-6 space-y-6 sm:p-6">
                          {
                            <div className="flex items-center justify-between">
                              <label
                                htmlFor="referenceNumber"
                                className="block mb-2 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal after:content-['*'] after:ml-0.5 after:text-red-500"
                              >
                                BRS Reference Number
                              </label>
                              <div className="flex flex-col w-full">
                                <Field
                                  name="brsReferenceNumber"
                                  id="referenceNumber"
                                  placeholder="Add BRS Reference Number"
                                  component={Input}
                                  className="w-full p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                                  disabled={transaction?.resolvedFields.includes(
                                    "brsReferenceNumber"
                                  )}
                                />
                              </div>
                            </div>
                          }

                          <div className="mb-4">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">
                              Upload documents
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Please provide the correct documents as
                              instructed.
                            </p>
                          </div>
                          <Field
                            name="fileUploads"
                            component={FileUploadForm}
                            resolvedFields={_get(transaction, "resolvedFields")}
                            documentData={[
                              ...documents.map((document) => document),
                            ]}
                          />

                          {transaction?.status ===
                            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
                              .AWAITING_COURT_ORDER && (
                            <div className="flex flex-col space-y-2">
                              <Checkbox
                                name="upholdConversionResolution"
                                label="Uphold the conversion resolution"
                                className="ml-0"
                                checked={values.upholdConversionResolution}
                                onChange={(e) => {
                                  setFieldValue(
                                    "upholdConversionResolution",
                                    e.target.checked
                                  );
                                  if (e.target.checked) {
                                    setFieldValue(
                                      "cancelConversionResolution",
                                      false
                                    ); // Uncheck the other
                                  }
                                }}
                              />
                              <Checkbox
                                name="cancelConversionResolution"
                                label="Cancel the conversion resolution"
                                className="ml-0"
                                checked={values.cancelConversionResolution}
                                onChange={(e) => {
                                  setFieldValue(
                                    "cancelConversionResolution",
                                    e.target.checked
                                  );
                                  if (e.target.checked) {
                                    setFieldValue(
                                      "upholdConversionResolution",
                                      false
                                    ); // Uncheck the other
                                  }
                                }}
                              />
                              {touched.upholdConversionResolution &&
                                touched.cancelConversionResolution &&
                                errors.checkboxes && (
                                  <div className="text-red-500">
                                    {errors.checkboxes}
                                  </div>
                                )}
                            </div>
                          )}

                          {transaction?.status ===
                            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES
                              .PENDING_REGISTRAR_DECISION && (
                            <div className="flex flex-col space-y-2">
                              <p className="mt-1 text-sm text-gray-500">
                                Please select the decision of the registrar on
                                company restoration
                              </p>
                              <div className="flex space-x-10">
                                <Checkbox
                                  name="registrarApproved"
                                  label="Registrar Approved "
                                  className="ml-0"
                                  checked={values.registrarApproved}
                                  onChange={(e) => {
                                    setFieldValue(
                                      "registrarApproved",
                                      e.target.checked
                                    );
                                    if (e.target.checked) {
                                      setFieldValue("registrarRejected", false); // Uncheck the other

                                      //also revome the registrar rejected file uploaded from the fileuploads field
                                      setFieldValue(
                                        "fileUploads.letterOfRejection",
                                        undefined
                                      );
                                    }
                                  }}
                                />
                                <Checkbox
                                  name="registrarRejected"
                                  label="Registrar Rejected"
                                  className="ml-0"
                                  checked={values.registrarRejected}
                                  onChange={(e) => {
                                    setFieldValue(
                                      "registrarRejected",
                                      e.target.checked
                                    );
                                    if (e.target.checked) {
                                      setFieldValue("registrarApproved", false); // Uncheck the other

                                      //also revome the registrar rejected file uploaded from the fileuploads field
                                      setFieldValue(
                                        "fileUploads.gazetteNotice",
                                        undefined
                                      );
                                    }
                                  }}
                                />
                              </div>
                              {touched.registrarApproved &&
                                touched.registrarRejected &&
                                errors.checkboxes2 && (
                                  <div className="text-red-500">
                                    {errors.checkboxes2}
                                  </div>
                                )}

                              {values.registrarApproved && (
                                <Field
                                  name="fileUploads"
                                  component={FileUploadForm}
                                  documentData={[
                                    {
                                      label: "Gazette Notice",
                                      name: "gazetteNotice",
                                      accept: "application/pdf",
                                    },
                                  ]}
                                />
                              )}
                              {values.registrarRejected && (
                                <Field
                                  name="fileUploads"
                                  component={FileUploadForm}
                                  documentData={[
                                    {
                                      label: "Letter of Rejection",
                                      name: "letterOfRejection",
                                      accept: "application/pdf",
                                    },
                                  ]}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  }
                </div>

                {
                  <div className="flex justify-between space-x-4 mt-6">
                    <button
                      onClick={handleGoBack}
                      className="flex items-start float-right py-2 font-semibold rounded text-primary"
                    >
                      <span className="flex items-center mr-2 h-5">
                        <ChevronLeftIcon
                          className="flex-shrink-0 w-5 h-5"
                          aria-hidden="true"
                        />
                      </span>
                      <p className="font-semibold">Go back</p>
                    </button>

                    {
                      <div>
                        {isSubmitting ? (
                          <button
                            type="submit"
                            className="flex items-center justify-center px-4 py-2 font-semibold text-white bg-sflPrimary rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                            disabled={isSubmitting}
                          >
                            <SpinnerIcon /> Submitting...
                          </button>
                        ) : !isLastStep ? (
                          <button
                            type="submit"
                            className="px-4 py-2 font-semibold text-white bg-sflPrimary rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                            disabled={isSubmitting}
                          >
                            Save and Proceed
                          </button>
                        ) : (
                          isLastStep && (
                            <button
                              type="submit"
                              className="px-4 py-2 font-semibold text-white bg-sflPrimary rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                              disabled={isSubmitting}
                            >
                              {"Save and proceed"}
                            </button>
                          )
                        )}
                      </div>
                    }
                  </div>
                }
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default TransactionUpload;

// ---- save ----
function saveFileToLocalStorage(file, storageKey) {
  const reader = new FileReader();
  reader.onload = () => {
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        name: file.name,
        type: file.type,
        lastModified: file.lastModified,
        dataUrl: reader.result,
      })
    );
  };
  reader.readAsDataURL(file);
}
function PersistFileUploadsToLocalStorage({ fileUploads, storageKey }) {
  useEffect(() => {
    if (!fileUploads || Object.keys(fileUploads).length === 0) return;

    Object.entries(fileUploads).forEach(([key, value]) => {
      if (!(value instanceof File)) return;

      const reader = new FileReader();
      reader.onload = () => {
        const existing = JSON.parse(localStorage.getItem(storageKey) || "{}");

        localStorage.setItem(
          storageKey,
          JSON.stringify({
            ...existing,
            [key]: {
              path: value?.path,
              name: value?.name,
              type: value?.type,
              lastModified: value?.lastModified,
              dataUrl: reader.result,
            },
          })
        );
      };
      reader.readAsDataURL(value);
    });
  }, [fileUploads, storageKey]);

  return null;
}

// ---- load ----
function getFileFromLocalStorage(storageKey) {
  const metaRaw = localStorage.getItem(storageKey);
  if (!metaRaw) return null;

  const { name, type, lastModified, dataUrl } = JSON.parse(metaRaw);
  const base64 = dataUrl.split(",")[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

  return new File([bytes], name, { type, lastModified });
}
